import { PlusIcon } from "@xola/icons";
import { uniqueId } from "lodash";
import React, { useMemo, useState } from "react";
import {
    Availability,
    BaseScheduleRepeat,
    BaseScheduleType,
    CheckInValidation,
    CheckInValidationType,
    Schedule,
} from "types";
import { BaseScheduleFilterType, defaultValues } from "@/components/BaseSchedule/BaseScheduleInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ScheduleValidationTable } from "@/components/Product/ExperienceCheckIn/CheckInWindow/CheckInWindowTable";
import {
    FilterButtonGroup,
    PurchaseCutoffContent,
} from "@/components/Product/ExperienceCheckIn/CheckInWindow/PurchaseCutoffContent";
import {
    CheckInWindowValidation,
    CutoffFormValues,
    CutoffRepeat,
    CutoffScheduleFormValues,
} from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";
import { useCheckInValidationField } from "@/components/Product/ExperienceCheckIn/useCheckInValidationField";
import { useDeleteWindowConfirmationModal } from "@/components/Product/ExperienceCheckIn/useDeleteWindowConfirmationModal";
import { useEditWindowConfirmationModal } from "@/components/Product/ExperienceCheckIn/useEditWindowConfirmationModal";
import { RepeatOption } from "@/components/Product/SchedulesAvailability/ScheduleEditor";
import { ScheduleEditorModal } from "@/components/Product/SchedulesAvailability/ScheduleEditorModal";
import { useVisibility } from "@/hooks/useVisibility";
import { isActive } from "@/models/Schedule";

interface PurchaseCutoffScheduleInputProps {
    validations: CheckInValidation[];
}

const cutoffDefaultValues: CutoffScheduleFormValues = { ...defaultValues, allowedPrivacies: [], duration: "P1D" };

export const PurchaseCutoffScheduleInput = ({ validations }: PurchaseCutoffScheduleInputProps) => {
    const { setValidations } = useCheckInValidationField("checkInPreference.");
    const { DeleteWindowConfirmationModal, confirmDeleteWindow } = useDeleteWindowConfirmationModal();
    const { EditWindowConfirmationModal, confirmEditWindow } = useEditWindowConfirmationModal();

    const [filter, setFilter] = useState(BaseScheduleFilterType.ALL);
    const [isScheduleEditorOpen, { show: showScheduleEditor, hide: hideScheduleEditor }] = useVisibility(false);
    const [validationToBeEdited, setValidationToBeEdited] = useState<CheckInWindowValidation | null>(null);

    const filteredValidations = useMemo(
        () => filterCheckInWindowValidations(validations, filter),
        [filter, validations],
    );

    const repeatOptions: RepeatOption[] = useMemo(() => {
        return [
            {
                value: CutoffRepeat.PURCHASE_CUTOFF,
                label: "Relative to purchase date",
                error: false,
                slots: {
                    Content: PurchaseCutoffContent,
                },
            },
        ];
    }, []);

    const handleAdd = () => {
        showScheduleEditor();
    };

    const handleEdit = async (validation: CheckInWindowValidation) => {
        if (!(await confirmEditWindow())) return;

        showScheduleEditor();
        setValidationToBeEdited(validation);
    };

    const handleApply = async (schedule: CutoffScheduleFormValues) => {
        const validation = getValidationPayload(schedule, validationToBeEdited?.id ?? getDraftId());
        let hasValidation = false;

        const mutatedValidations = validations.map((_validation) => {
            if (_validation.id && _validation.id === validation.id) {
                hasValidation = true;
                return validation;
            }

            return _validation;
        });

        if (!hasValidation) {
            mutatedValidations.push(validation);
        }

        setValidations(mutatedValidations);
    };

    const handleClose = () => {
        setValidationToBeEdited(null);
        hideScheduleEditor();
    };

    const handleRemove = async (validation: CheckInWindowValidation) => {
        if (!(await confirmDeleteWindow())) return;
        setValidations(validations.filter((_validation) => _validation.id !== validation.id));
    };

    return (
        <div className="ml-10">
            <div className="my-2 flex items-center justify-between">
                <FilterButtonGroup validations={validations} type={filter} onChange={setFilter} />
                <SecondaryButton icon={<PlusIcon size="small" />} onClick={handleAdd}>
                    Add window
                </SecondaryButton>
            </div>

            <ScheduleEditorModal<CutoffFormValues>
                disablePastDays
                isNotResourceSchedule={false}
                title="Check-In Validity Window"
                isOpen={isScheduleEditorOpen}
                initialValues={getInitialValues(validationToBeEdited)}
                repeatOptions={repeatOptions}
                onApply={handleApply}
                onClose={handleClose}
            />

            <ScheduleValidationTable validations={filteredValidations} onEdit={handleEdit} onRemove={handleRemove} />

            <DeleteWindowConfirmationModal />

            <EditWindowConfirmationModal />
        </div>
    );
};

const getInitialValues = (validation: CheckInWindowValidation | null): CutoffScheduleFormValues => {
    if (!validation) return cutoffDefaultValues;

    if (validation.object === CheckInValidationType.SCHEDULE) {
        return validation.schedule;
    }

    if (validation.object === CheckInValidationType.PURCHASE_CUTOFF) {
        return {
            ...cutoffDefaultValues,
            id: validation.id,
            type: validation.availability as unknown as BaseScheduleType,
            repeat: CutoffRepeat.PURCHASE_CUTOFF,
            duration: validation.duration,
            name: validation.name ?? "",
        };
    }

    return cutoffDefaultValues;
};

export const filterCheckInWindowValidations = (
    validations: CheckInValidation[],
    filter: BaseScheduleFilterType = BaseScheduleFilterType.ALL,
) => {
    const scheduleValidations = validations.filter((validation) =>
        [CheckInValidationType.SCHEDULE, CheckInValidationType.PURCHASE_CUTOFF].includes(validation.object),
    );

    if (filter === BaseScheduleFilterType.ALL) {
        return scheduleValidations;
    }

    return scheduleValidations.filter((validation) => {
        if (validation.object !== CheckInValidationType.SCHEDULE) {
            return filter === BaseScheduleFilterType.ACTIVE;
        }

        return filter === BaseScheduleFilterType.ACTIVE
            ? isActive(validation.schedule)
            : !isActive(validation.schedule);
    });
};

const getValidationPayload = (data: CutoffScheduleFormValues, validationId?: ID): CheckInValidation => {
    if (data.repeat === CutoffRepeat.PURCHASE_CUTOFF) {
        return {
            id: validationId,
            object: CheckInValidationType.PURCHASE_CUTOFF,
            name: data.name,
            duration: data.duration ?? "",
            availability: data.type as unknown as Availability,
        };
    }

    if (data.repeat === BaseScheduleRepeat.CUSTOM) {
        data.start = null;
        data.end = null;
    }

    return {
        id: validationId,
        object: CheckInValidationType.SCHEDULE,
        schedule: data as Schedule,
    };
};

const getDraftId = () => uniqueId("draft:");
